import { createContext, useContext, ReactNode } from 'react';
import styled from 'styled-components';
import { Workspace } from '@agency-smm/shared';
import { UseBrandFormResult } from '../../hooks/brand/useBrandForm';
import { UseBrandLivePersistResult } from '../../hooks/brand/useBrandLivePersist';
import { useAssetManagement } from '../../hooks/workspacePage/useAssetManagement';
import { useBackgroundImageManagement } from '../../hooks/workspacePage/useBackgroundImageManagement';

// Re-export de tipos y constantes para que las tabs importen todo desde acá.
export type {
  BrandFormData,
  AISection,
  BrandTagField,
  VoiceTagField,
  UseBrandFormResult,
} from '../../hooks/brand/useBrandForm';
export { SECTION_LABELS } from '../../hooks/brand/useBrandForm';
export type { UseBrandLivePersistResult, LivePersistField } from '../../hooks/brand/useBrandLivePersist';

// Resultados de los hooks de assets/backgrounds (tipos derivados del retorno).
export type AssetManagement = ReturnType<typeof useAssetManagement>;
export type BackgroundImageManagement = ReturnType<typeof useBackgroundImageManagement>;

/** Claves de las tabs (compartido entre BrandPage y las tabs para navegar). */
export type BrandTabKey = 'overview' | 'identity' | 'voice' | 'visual' | 'assets' | 'sources' | 'manual';

// ─── Contrato del contexto que consumen las 5 tabs ───────────────────────────
export interface BrandContextValue {
  /** Tab activa + setter (permite que el checklist de Overview navegue entre tabs). */
  activeTab: BrandTabKey;
  setActiveTab: (tab: BrandTabKey) => void;
  /** ID del workspace activo. */
  workspaceId: string | undefined;
  /** Workspace cargado (para logo/postsCount/owned). Null mientras carga. */
  workspace: Workspace | null;
  /** El usuario actual es dueño del workspace (gating de acciones owner-only). */
  isOwner: boolean;
  /** El usuario actual es admin (gating de borrado de fonts). */
  isAdmin: boolean;
  /** Estado + helpers del formulario de marca (la fuente de verdad). */
  form: UseBrandFormResult;
  /** Live-persist de logo scale/offset + forceBrandColors. */
  live: UseBrandLivePersistResult;
  /** CRUD de assets de marca (logos + librería). */
  assets: AssetManagement;
  /** CRUD de imágenes de fondo. */
  backgrounds: BackgroundImageManagement;
  /** Refresca el workspace (postsCount, logoUrl, etc.) tras live-persist. */
  reloadWorkspace: () => Promise<void>;
  /** Abre el modal de borrado de workspace (lo dispara la Danger Zone de Overview). */
  openDeleteModal: () => void;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

export function BrandProvider({ value, children }: { value: BrandContextValue; children: ReactNode }) {
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

/** Contrato que consumen las tabs. Lanza si se usa fuera del BrandProvider. */
export function useBrandContext(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    throw new Error('useBrandContext must be used within a BrandProvider');
  }
  return ctx;
}

// ─── Styled components compartidos ───────────────────────────────────────────

/** Grid responsive de 2 columnas que colapsa a 1 en tablet. */
export const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

/** Header de card con título a la izquierda y (opcional) botón AI a la derecha. */
export const SectionHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
`;

/** Botón ✨ de AI fill — usado en headers de sección. */
export const AiFillButton = styled.button`
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 2px; }
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// ─── Tag input (chips) ───────────────────────────────────────────────────────
export const TagInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
  border: ${({ theme }) => theme.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-height: 42px;
  background: ${({ theme }) => theme.colors.surface};
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

export const TagRemove = styled.button`
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 1px; }
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0 2px;
  font-size: 14px;
  line-height: 1;
  font-weight: 700;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

export const TagInputField = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

// ─── Color swatches + fonts ──────────────────────────────────────────────────
export const SwatchRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export const ColorSwatch = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $color }) => $color};
  border: 2px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  position: relative;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:hover::after {
    content: '\\00d7';
    position: absolute;
    top: -6px;
    right: -6px;
    background: ${({ theme }) => theme.colors.error};
    color: white;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }
`;

export const ColorPickerInput = styled.input`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
`;

export const FontTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

export const FontTagRemove = styled.button`
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 1px; }
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: 0 2px;
  font-size: 14px;
  line-height: 1;
  font-weight: 700;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

/** Botón compacto "+ Add" para colores/fonts. */
export const AddButton = styled.button`
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 2px; }
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

export const AddColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const HintText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

// ─── Slider row (content mix + logo controls) ────────────────────────────────
export const SliderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const SliderLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const SliderLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

export const SliderHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const SliderValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  min-width: 44px;
  text-align: right;
`;

export const RangeSlider = styled.input`
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    transition: transform 0.15s ease;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    border: none;
    cursor: pointer;
  }
`;

/** Total advisory del content mix (verde si 100, rojo si no). */
export const TotalAdvisory = styled.div<{ $isValid: boolean }>`
  text-align: right;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme, $isValid }) => ($isValid ? theme.colors.success : theme.colors.error)};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

// ─── Advanced <details> disclosure (campos orphan/avanzados) ──────────────────
export const Advanced = styled.details`
  margin-top: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};

  > summary {
    cursor: pointer;
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.textSecondary};
    list-style: none;
    user-select: none;
  }

  > summary::-webkit-details-marker {
    display: none;
  }

  > summary::before {
    content: '▸ ';
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &[open] > summary::before {
    content: '▾ ';
  }

  > div {
    margin-top: ${({ theme }) => theme.spacing.md};
  }
`;

/** Tick inline "Saved" para acciones de live-persist. */
export const SavedTick = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.success};
`;

/** Caja de sección con título + borde inferior (subsecciones dentro de cards). */
export const SubSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const SubSectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

// ─── Helper de teclado para tag inputs (Enter / coma agregan) ─────────────────
/**
 * Devuelve un onKeyDown que agrega el valor al presionar Enter o coma y limpia
 * el input local. Las tabs lo usan con cualquier addTag/addVoiceTag.
 */
export function makeTagKeyDown(
  onAdd: (value: string) => void,
  inputValue: string,
  clearInput: () => void,
) {
  return (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      onAdd(inputValue);
      clearInput();
    }
  };
}
